/**
 * ImpactBlock — long-form Business Impact paragraph rendered inside the
 * Workflow Selector's expanded-row detail (per spec §2.4.2 and §2.7.2).
 *
 *   Header: small `Impact` label only.
 *   Body:   paragraph text or muted empty-state, click-to-edit (textarea).
 *
 * The block is only mounted by FlowTable when `supportsBusinessImpact(...)`
 * is true. For deferred workflow types the parent renders nothing, so we do
 * not need to defensively guard here — but we still handle the empty-data
 * case (supported workflow, no answers yet) by showing the muted "no impact
 * summary yet" message.
 */

import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { generateBusinessImpact } from '../api/businessImpactFunctions';
import { toUserFacingError } from '../utils/userFacingError';
import InlineEditableText from './InlineEditableText';
import type { FlowMetadata } from '../types/flow';
import type {
  BusinessImpactPatchPayload,
  BusinessImpactPatchResponse,
} from '../api/businessImpactFunctions';

export interface ImpactBlockProps {
  flow: FlowMetadata;
  onPatch: (
    flowId: string,
    payload: BusinessImpactPatchPayload
  ) => Promise<BusinessImpactPatchResponse>;
  /**
   * Called after a successful `onPatch`. The parent (FlowTable) forwards this
   * up so `flows[i].businessImpact` can be merged with the server's response,
   * avoiding the "summary disappears after save" bug where `InlineEditableText`
   * re-renders with the stale `value` prop.
   */
  onUpdated?: (patch: NonNullable<FlowMetadata['businessImpact']>) => void;
}

/**
 * Stage label that the user should visit to seed Business Impact answers.
 * Mirrors spec §2.4.2.
 */
function stageLabelFor(workflowType: string | undefined): string {
  if (['services', 'complex_services'].includes((workflowType || '').toLowerCase())) {
    return 'Context Questions';
  }
  return 'Generate Questions';
}

export const ImpactBlock: React.FC<ImpactBlockProps> = ({ flow, onPatch, onUpdated }) => {
  const [refreshing, setRefreshing] = React.useState(false);
  const [refreshError, setRefreshError] = React.useState<string | null>(null);
  const bi = flow.businessImpact ?? {};
  const services = ['services', 'complex_services'].includes(flow.workflow_type || '');
  const needsRefresh = services && (bi.stale || ['stale', 'pending', 'failed'].includes(bi.status || ''));
  const paragraph = bi.summaryParagraph ?? '';
  const hasParagraph = paragraph.trim() !== '';
  const stageLabel = stageLabelFor(flow.workflow_type);
  const refresh = async () => {
    setRefreshing(true); setRefreshError(null);
    try { onUpdated?.(await generateBusinessImpact(flow.id)); }
    catch (error) { setRefreshError(toUserFacingError(error)); }
    finally { setRefreshing(false); }
  };

  const handleSave = React.useCallback(
    async (next: string) => {
      const resp = await onPatch(flow.id, { summaryParagraph: next });
      // Seed with the user's value so the parent always sees the new text,
      // then let the server's authoritative response override if different.
      onUpdated?.({ summaryParagraph: next, ...resp });
    },
    [flow.id, onPatch, onUpdated]
  );

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        Impact
      </Typography>
      {needsRefresh && <Alert severity="info" sx={{ mt: 1 }}>
        Impact summary needs review against your current answers.
        {bi.reconciliationRequired ? ' Your manual edits were preserved. Review and save your summary against the current answers.'
          : <Button size="small" disabled={refreshing} onClick={() => void refresh()}>Refresh impact summary</Button>}
      </Alert>}
      {refreshError && <Alert severity="error" sx={{ mt: 1 }}>{refreshError}</Alert>}

      <Box sx={{ mt: 0.5 }}>
        {hasParagraph ? (
          <InlineEditableText
            value={paragraph}
            onSave={handleSave}
            variant="multi"
            ariaLabel="Edit impact paragraph"
          />
        ) : (
          // Empty-state: muted prompt to answer BI questions, AND a click-to-
          // edit affordance so the user can hand-write a paragraph if they
          // prefer.
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: 'italic', mb: 0.5 }}
            >
              {needsRefresh ? 'Your saved context needs a summary refresh; you do not need to answer the questions again.' : `No impact summary yet — answer the Business Impact questions in the ${stageLabel} stage to generate one.`}
            </Typography>
            <InlineEditableText
              value=""
              onSave={handleSave}
              variant="multi"
              emptyPlaceholder="Add an impact summary"
              ariaLabel="Edit impact paragraph"
            />
          </Box>
        )}
      </Box>

    </Box>
  );
};

export default ImpactBlock;
